import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Eye, EyeOff, Clock, Vote, ArrowLeft } from 'lucide-react';
import { Button } from '../components/Button';
import { Card } from '../components/Card';
import { LanguageSelector } from '../components/LanguageSelector';
import { GameState, Player } from '../types';

interface GamePageProps {
  gameState: GameState;
  player: Player;
  onBack: () => void;
  onStartVoting: () => void;
}

export const GamePage: React.FC<GamePageProps> = ({
  gameState,
  player,
  onBack,
  onStartVoting,
}) => {
  const { t } = useTranslation();
  const [timeLeft, setTimeLeft] = useState(120); // 2 minutes
  const [showWord, setShowWord] = useState(false);
  const [isImpostor] = useState(gameState.roles[player.id] === 'impostor');

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          onStartVoting();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [onStartVoting]);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-purple-900 flex flex-col items-center justify-center p-4 relative overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg%20width%3D%2260%22%20height%3D%2260%22%20viewBox%3D%220%200%2060%2060%22%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%3E%3Cg%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%3Cg%20fill%3D%22%239C92AC%22%20fill-opacity%3D%220.1%22%3E%3Ccircle%20cx%3D%2230%22%20cy%3D%2230%22%20r%3D%222%22/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')] opacity-20 pointer-events-none"></div>
      
      {/* Language Selector */}
      <div className="absolute top-4 right-4 z-10">
        <LanguageSelector />
      </div>

      {/* Back Button */}
      <div className="absolute top-4 left-4 z-10">
        <Button
          variant="secondary"
          size="sm"
          onClick={onBack}
          icon={<ArrowLeft className="w-4 h-4" />}
        >
          {t('common.back')}
        </Button>
      </div>

      {/* Main Content */}
      <div className="w-full max-w-4xl animate-fade-in">
        <div className="text-center mb-8">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4 font-display">
            {t('game.title')}
          </h1>
        </div>

        {/* Timer */}
        <Card className="mb-6 text-center">
          <div className="flex items-center justify-center gap-3 mb-4">
            <Clock className="w-6 h-6 text-white" />
            <h2 className="text-2xl font-bold text-white">
              {t('game.timeRemaining', { time: formatTime(timeLeft) })}
            </h2>
          </div>
          <div className="w-full bg-white/10 rounded-full h-3">
            <div
              className="bg-gradient-to-r from-primary-500 to-primary-600 h-3 rounded-full transition-all duration-1000"
              style={{ width: `${(timeLeft / 120) * 100}%` }}
            />
          </div>
        </Card>

        {/* Role Card */}
        <Card className={`mb-6 border-2 ${
          isImpostor 
            ? 'border-accent-400 bg-accent-500/10' 
            : 'border-success-400 bg-success-500/10'
        }`}>
          <div className="text-center">
            <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${
              isImpostor 
                ? 'bg-accent-500' 
                : 'bg-success-500'
            }`}>
              {isImpostor ? (
                <EyeOff className="w-8 h-8 text-white" />
              ) : (
                <Eye className="w-8 h-8 text-white" />
              )}
            </div>
            <h3 className="text-2xl font-bold text-white mb-2">
              {t('game.yourRole')}
            </h3>
            <p className={`text-xl font-semibold ${
              isImpostor ? 'text-accent-400' : 'text-success-400'
            }`}>
              {isImpostor ? t('game.impostor') : t('game.player')}
            </p>
          </div>
        </Card>

        {/* Word Card */}
        <Card className="mb-6">
          <div className="text-center">
            <h3 className="text-xl font-semibold text-white mb-4">
              {t('game.category')}: {gameState.category}
            </h3>
            
            <div className="bg-white/10 rounded-xl p-6 mb-4">
              <div className="flex items-center justify-center gap-3 mb-3">
                <span className="text-white/60">🔤</span>
                <span className="text-white/60">{t('game.word')}</span>
              </div>
              
              {showWord || !isImpostor ? (
                <p className="text-3xl font-bold text-white">
                  {gameState.word}
                </p>
              ) : (
                <div className="flex items-center justify-center gap-2">
                  <div className="w-8 h-8 bg-white/20 rounded animate-pulse"></div>
                  <div className="w-8 h-8 bg-white/20 rounded animate-pulse"></div>
                  <div className="w-8 h-8 bg-white/20 rounded animate-pulse"></div>
                  <div className="w-8 h-8 bg-white/20 rounded animate-pulse"></div>
                </div>
              )}
            </div>

            {isImpostor && (
              <Button
                variant="accent"
                size="md"
                onClick={() => setShowWord(!showWord)}
                icon={showWord ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              >
                {showWord ? 'Ocultar palabra' : 'Mostrar palabra'}
              </Button>
            )}
          </div>
        </Card>

        {/* Instructions */}
        <Card className="mb-6">
          <div className="text-center">
            <h3 className="text-lg font-semibold text-white mb-3">
              Instrucciones
            </h3>
            <p className="text-white/80 leading-relaxed">
              {isImpostor 
                ? t('game.impostorInstructions')
                : t('game.playerInstructions')
              }
            </p>
          </div>
        </Card>

        {/* Start Voting Button */}
        <div className="text-center">
          <Button
            variant="primary"
            size="xl"
            onClick={onStartVoting}
            icon={<Vote className="w-6 h-6" />}
            className="px-12 py-6 text-xl"
          >
            {t('game.startVoting')}
          </Button>
        </div>
      </div>
    </div>
  );
};
